import { useMemo, useState } from 'react'

import { getNativeAssetInfo } from '@/config/chain/utils/getNativeAssetInfo'
import { E_EARN_VAULT_CHAIN_ID, E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { healthFactorToRiskLevel } from '@/domain/common/risk'
import { EModeCategoryId } from '@/domain/e-mode/types'
import { useEBorrowPositions } from '@/domain/euler/positions/useEBorrowPositions'
import { useELendPositions } from '@/domain/euler/positions/useELendPositions'
import { useEVaultPositions } from '@/domain/euler/positions/useEVaultPositions'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { useIsolatedHealthFactor } from '@/features/isolated-details/logic/useIsolatedHealthFactor'
import { useMyPortfolioForPairs as useIsolatedPortfolioForPairs } from '@/features/isolated-portfolio/logic/useMyPortfolio'
import { getBorrows as getPooledBorrows, getDeposits as getPooledDeposits } from '@/features/my-portfolio/logic/assets'
import { makeLiquidationDetails } from '@/features/my-portfolio/logic/makeLiquidationDetails'
import { makePositionSummary } from '@/features/my-portfolio/logic/position'
import { makeWalletComposition } from '@/features/my-portfolio/logic/wallet-composition'
import {
  formatTokenData,
  getGroupedByPairAddress,
  getLowestHealthFactor,
  getTotalIsolatedUSD,
  usdFormatter,
  usdFormatterCompact,
} from '@/features/side-drawer/logic/utils'
import BigNumber from 'bignumber.js'

import type { UnifiedPortfolioLoadingStates, UseUnifiedPortfolioResult } from './types'
import { usePortfolioEvmBalances } from './usePortfolioEvmBalances'
import { usePortfolioL1Balances } from './usePortfolioL1Balances'

function calculatePooledHealthFactor({
  totalCollateralUSD,
  currentLiquidationThreshold,
  totalBorrowsUSD,
}: {
  totalCollateralUSD: NormalizedUnitNumber
  currentLiquidationThreshold: BigNumber
  totalBorrowsUSD: NormalizedUnitNumber
}): BigNumber {
  if (totalBorrowsUSD.eq(0)) return new BigNumber(Number.POSITIVE_INFINITY)
  if (totalCollateralUSD.eq(0)) return new BigNumber(0)

  return totalCollateralUSD.multipliedBy(currentLiquidationThreshold).dividedBy(totalBorrowsUSD)
}

export function useUnifiedPortfolio({ refetchInterval }: { refetchInterval?: number } = {}): UseUnifiedPortfolioResult {
  const { chainId } = usePageChainId()
  const nativeAssetInfo = getNativeAssetInfo(chainId)
  const [compositionWithDeposits, setCompositionWithDeposits] = useState(true)

  const evmBalances = usePortfolioEvmBalances({ refetchInterval })
  const l1Balances = usePortfolioL1Balances({ refetchInterval })
  const { marketInfo, walletInfo } = evmBalances
  const pairs = evmBalances.isolatedPairs
  const {
    deposits: isolatedDeposits,
    borrows: isolatedBorrows,
    isLoading: isIsolatedPositionsLoading,
  } = useIsolatedPortfolioForPairs({ pairs, isPairsLoading: evmBalances.loading.isolatedPairs })

  const { positions: elendPositions, isLoading: isELendLoading } = useELendPositions({ chainId: E_LEND_CHAIN_ID })
  const { positions: eborrowPositions, isLoading: isEBorrowLoading } = useEBorrowPositions({ chainId: E_LEND_CHAIN_ID })
  const { positions: earnPositions, isLoading: isEVaultLoading } = useEVaultPositions({
    chainId: E_EARN_VAULT_CHAIN_ID,
  })

  const {
    pairs: pairsWithHealth,
    minHealthFactor: isolatedMinHealthFactor,
    hasUnsafePositions,
    isLoading: isIsolatedHealthLoading,
  } = useIsolatedHealthFactor({
    pairAddresses: pairs.map((pair) => pair.address),
  })

  const pooledDeposits = useMemo(() => {
    if (!marketInfo) return []
    return getPooledDeposits({
      marketInfo,
      walletInfo,
      nativeAssetInfo,
      chainId,
    })
  }, [chainId, marketInfo, nativeAssetInfo, walletInfo])

  const pooledBorrows = useMemo(() => {
    if (!marketInfo) return []
    return getPooledBorrows({ marketInfo, nativeAssetInfo, chainId })
  }, [chainId, marketInfo, nativeAssetInfo])

  const pooledPositionSummary = useMemo(() => {
    if (!marketInfo) return undefined
    const positionSummary = makePositionSummary({ marketInfo })
    return {
      ...positionSummary,
      healthFactor: calculatePooledHealthFactor(marketInfo.userPositionSummary),
    }
  }, [marketInfo])

  const pooledWalletComposition = useMemo(() => {
    if (!marketInfo) return undefined
    return makeWalletComposition({
      marketInfo,
      walletInfo,
      compositionWithDeposits,
      setCompositionWithDeposits,
      nativeAssetInfo,
      chainId: marketInfo.chainId,
    })
  }, [compositionWithDeposits, marketInfo, nativeAssetInfo, walletInfo])

  const pooledEModeCategoryId = (
    marketInfo?.userConfiguration.eModeState.enabled ? marketInfo.userConfiguration.eModeState.category.id : 0
  ) as EModeCategoryId

  const pooledLiquidationDetails = useMemo(() => {
    if (!marketInfo) return undefined
    return makeLiquidationDetails(marketInfo)
  }, [marketInfo])

  const totalEVMTokenBalanceUSD = evmBalances.balances.reduce(
    (acc, t) => NormalizedUnitNumber(acc.plus(t.token.unitPriceUsd.multipliedBy(t.balance))),
    NormalizedUnitNumber(0),
  )
  const totalL1TokenBalanceUSD = l1Balances.balances.reduce(
    (acc, t) => NormalizedUnitNumber(acc.plus(t.token.unitPriceUsd.multipliedBy(t.balance))),
    NormalizedUnitNumber(0),
  )

  const totalPooledUSD = marketInfo
    ? marketInfo.userPositionSummary.totalCollateralUSD.minus(marketInfo.userPositionSummary.totalBorrowsUSD)
    : NormalizedUnitNumber(0)
  const totalIsolatedUSD = getTotalIsolatedUSD(isolatedBorrows, isolatedDeposits)
  const totalTokenBalanceUSD = totalEVMTokenBalanceUSD.plus(totalL1TokenBalanceUSD)

  const collateralKeys = new Set(
    eborrowPositions.map((p) => `${p.collateralVaultAddress.toLowerCase()}-${p.subAccountId}`),
  )
  const filteredElendPositions = elendPositions.filter(
    (p) => !collateralKeys.has(`${p.vaultAddress.toLowerCase()}-${p.subAccountId}`),
  )

  const totalEulerDepositUSD = [...filteredElendPositions, ...earnPositions].reduce(
    (acc, p) => NormalizedUnitNumber(acc.plus(p.token.unitPriceUsd.multipliedBy(p.depositAmount))),
    NormalizedUnitNumber(0),
  )
  const totalEulerBorrowNetUSD = eborrowPositions.reduce((acc, p) => {
    const collateralUSD = p.collateralToken.unitPriceUsd.multipliedBy(p.collateralAmount)
    const borrowUSD = p.liabilityToken.unitPriceUsd.multipliedBy(p.borrowAmount)
    return NormalizedUnitNumber(acc.plus(collateralUSD.minus(borrowUSD)))
  }, NormalizedUnitNumber(0))
  const totalEulerUSD = NormalizedUnitNumber(totalEulerDepositUSD.plus(totalEulerBorrowNetUSD))

  const lowestHealthFactor = getLowestHealthFactor(pooledPositionSummary?.healthFactor, isolatedMinHealthFactor)
  const currentHighestRiskLevel = healthFactorToRiskLevel(lowestHealthFactor)

  const loading: UnifiedPortfolioLoadingStates = {
    pooledMarket: evmBalances.loading.pooledMarket || l1Balances.loading.pooledMarket,
    evmWallet: evmBalances.loading.evmWallet,
    l1Wallet: l1Balances.loading.l1Wallet,
    hyperCoreAssets: evmBalances.loading.hyperCoreAssets || l1Balances.loading.hyperCoreAssets,
    extraEvmTokens: evmBalances.loading.extraEvmTokens,
    isolatedPairs: evmBalances.loading.isolatedPairs,
    isolatedPositions: isIsolatedPositionsLoading,
    isolatedHealth: isIsolatedHealthLoading,
    eulerLend: isELendLoading,
    eulerBorrow: isEBorrowLoading,
    eulerEarn: isEVaultLoading,
  }

  const isL1Loading = l1Balances.isLoading
  const isEvmLoading =
    evmBalances.isLoading ||
    loading.isolatedPositions ||
    loading.isolatedHealth ||
    loading.eulerLend ||
    loading.eulerBorrow ||
    loading.eulerEarn

  return {
    loading,
    isLoading: isEvmLoading || isL1Loading,
    isEvmLoading,
    isL1Loading,
    balances: {
      evmWallet: evmBalances.balances,
      l1Wallet: l1Balances.balances,
    },
    totalUSD: usdFormatter.format(totalPooledUSD.plus(totalIsolatedUSD).plus(totalEulerUSD).toNumber()),
    tokenData: {
      ...formatTokenData({
        chainId,
        walletTokens: evmBalances.balances,
        formattedL1Balances: l1Balances.balances,
        nativeAssetInfo,
      }),
      totalUSD: usdFormatter.format(totalTokenBalanceUSD.toNumber()),
    },
    positionData: {
      highestRiskLevel: currentHighestRiskLevel,
      pooled: {
        deposits: pooledDeposits.filter((reserve) => reserve.deposit.gt(0)),
        borrows: pooledBorrows.filter((reserve) => reserve.debt.gt(0)),
        allDeposits: pooledDeposits,
        allBorrows: pooledBorrows,
        healthFactor: pooledPositionSummary?.healthFactor,
        positionSummary: pooledPositionSummary,
        netApyDetails: marketInfo?.userNetApyDetails,
        walletComposition: pooledWalletComposition,
        eModeCategoryId: pooledEModeCategoryId,
        liquidationDetails: pooledLiquidationDetails,
        totalUSD: usdFormatterCompact.format(totalPooledUSD.toNumber()),
      },
      isolated: {
        deposits: isolatedDeposits,
        borrows: isolatedBorrows,
        totalUSD: usdFormatterCompact.format(totalIsolatedUSD.toNumber()),
        minHealthFactor: isolatedMinHealthFactor,
        hasUnsafePositions,
        groupedByPairAddress: getGroupedByPairAddress({
          pairs,
          isolatedDeposits,
          isolatedBorrows,
          pairsWithHealth,
        }),
      },
      euler: {
        elend: filteredElendPositions,
        eborrow: eborrowPositions,
        earn: earnPositions,
        totalUSD: usdFormatterCompact.format(totalEulerUSD.toNumber()),
      },
    },
  }
}
